import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { LogEntryWithType } from '@/types/TInbox';
import { getCallLogs, getVoiceMailLogs, getTextLogs } from '@/api/logs';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import LogsList from './components/LogList';
import LogDetails from './components/LogDetails';
import TabNavigation from './components/TabNavigation';
import { Loading } from '@/components/Loading';
import { useLogProcessor } from 'src/Hooks/useLogsProcessor';
import { usePersistentState } from 'src/Hooks/usePersistentState';
import { useTabCounts } from 'src/Hooks/useTabCount';
import IntakeFormsPanel from './components/IntakeFormPanel';
import IntakeFormsDisplay from './components/IntakeFormView';
import FilterControls from './components/Filters';
import Header from '@/components/Header/Header.js';
import { InboxOutlined } from '@ant-design/icons';
import { getProvisionedNumber } from '@/api/reservedNumbers';
import { DownloadType, FilterStatus } from 'src/enums/ELogs';
import { useDownloadHandler } from 'src/Hooks/useLogsDownloads';
import { useDeleteHandler } from 'src/Hooks/useLogsDelete';
import { useLocalTimezone } from 'src/common/hooks/useLocalTimezone';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useFeatureGating } from 'src/Hooks/useFeatureGating';
import { AppRoutes } from 'src/enums/ERoutes';
import { USER_TYPES } from 'src/enums/EUser';

interface InboxProps {
	debrand?: boolean;
	isNoviUser?: boolean;
}

const Inbox: React.FC<InboxProps> = ({ debrand = false, isNoviUser = false }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedLog, setSelectedLog] = useState<LogEntryWithType | null>(null);
	const [activeTab, setActiveTab] = useState('All Inbox');
	const [dateRange, setDateRange] = useState<[any, any] | null>(null);
	const [statusFilter, setStatusFilter] = useState<string>(FilterStatus.ALL);
	const [durationFilter, setDurationFilter] = useState<string>(FilterStatus.ALL);
	const [showingFormResponse, setShowingFormResponse] = useState(false);
	const [showMobileDetails, setShowMobileDetails] = useState(false);
	const [hideEmptyConvos, setHideEmptyConvos] = usePersistentState('hideEmptyConvos', true);

	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const startDate = dateRange?.[0] ? dateRange[0].valueOf() : null;
	const endDate = dateRange?.[1] ? dateRange[1].valueOf() : null;
	const { timeZone } = useLocalTimezone(selectedAgent?.time_zone);

	const isFormResponsesEnabled = useFeatureFlag(FeatureFlag.FORM_RESPONSES_PAGE);
	const isLegacyIntakeFormsEnabled = !isFormResponsesEnabled;
	const { isFeatureUnlimited, getFeatureNumericValue, isFeatureEnabled, getCurrentPlan } =
		useFeatureGating();

	const dispatch = useAppDispatch();
	const isLogsUnlimited =
		loggedInUser?.userType === USER_TYPES.SUB_ACCOUNT || selectedAgent?.type === 'Outbound'
			? true
			: isFeatureUnlimited('logs', 'AnalyticsAndReporting');
	const logsLimit = getFeatureNumericValue('logs', 'AnalyticsAndReporting');
	const isLogsEnabled = isFeatureEnabled('logs', 'AnalyticsAndReporting');
	const currentPlan = getCurrentPlan();
	const isBasicPlan = currentPlan === 'basic';

	const getCallLogsLimit = () => {
		if (!isLogsEnabled) {
			return 0;
		}
		if (isLogsUnlimited) {
			return 50;
		}
		if (logsLimit === 0) {
			return 0;
		}

		return Math.min(logsLimit, 50);
	};

	const callLogsPaginationLimit = getCallLogsLimit();

	const { data: provisionedNumbers } = useQuery({
		queryKey: ['getProvisionedNumbers', selectedAgentId],
		queryFn: () => getProvisionedNumber(selectedAgentId),
		enabled: !!selectedAgentId,
	});

	const {
		data: callLogsPages,
		refetch: refetchCallLogs,
		isLoading: isCallLogsLoading,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ['callLogs', selectedAgentId, startDate, endDate],
		queryFn: ({ pageParam = null }) => {
			// Calculate the actual limit for this specific API call
			let actualLimit = callLogsPaginationLimit;
			if (actualLimit === 0) {
				return {
					callLogs: [],
					lastKey: pageParam,
				};
			}

			// If we have a finite limit and we're not on the first page, calculate remaining
			if (!isLogsUnlimited && logsLimit > 0 && pageParam !== null) {
				const existingPages = callLogsPages?.pages || [];
				const currentCount = existingPages.reduce(
					(total, page) => total + (page.callLogs?.length || 0),
					0,
				);
				const remainingAllowed = logsLimit - currentCount;

				if (remainingAllowed > 0) {
					actualLimit = Math.min(remainingAllowed, callLogsPaginationLimit);
				} else {
					return {
						callLogs: [],
						lastKey: pageParam,
					};
				}
			}

			const params = { limit: actualLimit, lastKey: pageParam, startDate, endDate };
			return getCallLogs(selectedAgentId as string, params);
		},
		initialPageParam: null,
		getNextPageParam: (lastPage) => {
			return lastPage.lastKey || undefined;
		},
		enabled: !!selectedAgentId,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	});

	const {
		data: voicemailsData,
		refetch: refetchVoicemails,
		isLoading: isVoicemailsLoading,
	} = useQuery({
		queryKey: ['voicemails', selectedAgentId],
		queryFn: () => getVoiceMailLogs(selectedAgentId as string),
		enabled: !!selectedAgentId,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	});

	const {
		data: textLogsData,
		refetch: refetchTextLogs,
		isLoading: isTextLogsLoading,
	} = useQuery({
		queryKey: ['textLogs', selectedAgentId],
		queryFn: () => getTextLogs(selectedAgentId as string),
		enabled: !!selectedAgentId,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	});

	const { handleDownload, downloadCallLogsStatus } = useDownloadHandler();

	const {
		confirmDelete,
		logToDelete,
		isDeletingLog,
		handleDeleteLog,
		handleConfirmDelete,
		handleCancelDelete,
	} = useDeleteHandler(
		selectedLog,
		setSelectedLog,
		refetchCallLogs,
		refetchTextLogs,
		refetchVoicemails,
	);

	const callLogsDataFromPages = useMemo(() => {
		if (!callLogsPages) {
			return [];
		}
		return callLogsPages.pages.flatMap((page) => page.callLogs || []);
	}, [callLogsPages]);

	const { allLogs, formGroups } = useLogProcessor(
		callLogsDataFromPages,
		textLogsData,
		voicemailsData,
		dateRange,
	);

	const { filteredLogs, tabCounts, totalCallCount } = useTabCounts(
		allLogs,
		formGroups,
		activeTab,
		searchTerm,
		null,
		statusFilter,
		durationFilter,
		hideEmptyConvos,
		isLegacyIntakeFormsEnabled,
	);

	// Check if can load more call logs based on feature limits
	const canLoadMoreCallLogs = (() => {
		if (!isLogsEnabled) {
			return false;
		}
		if (isLogsUnlimited) {
			return true;
		}
		if (logsLimit === 0) {
			return false;
		}
		return totalCallCount < logsLimit;
	})();

	const canLoadMore = canLoadMoreCallLogs;

	const hasActiveFilters = !!(
		searchTerm ||
		dateRange ||
		statusFilter !== FilterStatus.ALL ||
		durationFilter !== FilterStatus.ALL
	);
	const isInitialLoading =
		(isCallLogsLoading || isTextLogsLoading || isVoicemailsLoading) &&
		!callLogsPages &&
		!textLogsData &&
		!voicemailsData;
	const isLoadingCallLogs = isCallLogsLoading;

	useEffect(() => {
		if (activeTab !== 'Intake Forms') {
			setShowingFormResponse(false);
		}
	}, [activeTab]);

	useEffect(() => {
		if (filteredLogs.length > 0 && !selectedLog && activeTab !== 'Intake Forms') {
			setSelectedLog(filteredLogs[0]);
		}
	}, [filteredLogs, selectedLog, activeTab]);

	useEffect(() => {
		const handleResize = () => {
			setShowMobileDetails(window.innerWidth < 1024);
		};
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleFormSelect = (formLog: LogEntryWithType) => {
		setSelectedLog(formLog);
		setShowingFormResponse(true);
	};

	const handleBackToForms = () => {
		setShowingFormResponse(false);
		setSelectedLog(null);
	};

	const handleLogSelect = (log: LogEntryWithType) => {
		setSelectedLog(log);
		if (window.innerWidth < 1024) {
			setShowMobileDetails(true);
		}
	};

	const handleBackToList = () => {
		setShowMobileDetails(false);
		setTimeout(() => {
			setSelectedLog(null);
		}, 100);
	};

	const handleRefreshLogs = () => {
		refetchCallLogs();
		refetchVoicemails();
		refetchTextLogs();
	};

	const onDownload = (type: DownloadType) => {
		const downloadParams = {
			startDate,
			endDate,
			isNoviUser,
			agentId: selectedAgentId,
			textLogs: textLogsData?.textLogs || [],
			voicemailLogs: voicemailsData?.voiceMailLogs || [],
			timeZone,
		};
		handleDownload(type, downloadParams);
	};

	const clearAllFilters = () => {
		setSearchTerm('');
		setDateRange(null);
		setStatusFilter(FilterStatus.ALL);
		setDurationFilter(FilterStatus.ALL);
	};

	const handleActionButtonClick = (action: 'upgrade' | 'contact') => {
		if (action === 'upgrade') {
			dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
		} else {
			window.open(
				`${import.meta.env.VITE_APP_BASE_URL}${AppRoutes.CONTACT_SALES}`,
				'_blank',
				'noreferrer',
			);
		}
	};

	if (isInitialLoading) {
		return (
			<div className="h-[calc(100vh-70px)] flex items-center justify-center">
				<Loading />
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh)] flex flex-col bg-gray-50">
			<Header
				title="Inbox"
				description="View and manage your inbox"
				icon={<InboxOutlined />}
				debrand={debrand}
			/>
			<div className="bg-white border-b border-gray-200 px-2 py-3">
				<div className="flex flex-col gap-3">
					<div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between gap-3">
						<TabNavigation
							activeTab={activeTab}
							onTabChange={setActiveTab}
							tabCounts={tabCounts}
							isLegacyIntakeFormsEnabled={isLegacyIntakeFormsEnabled}
						/>
						<div className="responsive-filter-controls">
							<FilterControls
								searchTerm={searchTerm}
								setSearchTerm={setSearchTerm}
								dateRange={dateRange}
								setDateRange={setDateRange}
								statusFilter={statusFilter}
								setStatusFilter={setStatusFilter}
								durationFilter={durationFilter}
								setDurationFilter={setDurationFilter}
								hasActiveFilters={hasActiveFilters}
								onRefresh={handleRefreshLogs}
								onDownload={onDownload}
								isLoading={isLoadingCallLogs}
								showActiveFilters={false}
								downloadCallLogsStatus={downloadCallLogsStatus}
								isNoviUser={isNoviUser}
								selectedAgentId={selectedAgentId}
								provisionedNumbers={provisionedNumbers}
								hideEmptyConvos={hideEmptyConvos}
								setHideEmptyConvos={setHideEmptyConvos}
							/>
						</div>
					</div>
				</div>

				{hasActiveFilters && (
					<div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
						<span className="text-sm text-gray-600">Active filters:</span>
						{searchTerm && (
							<span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
								Search: "{searchTerm}"
							</span>
						)}
						{dateRange && (
							<span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
								Date: {dateRange[0]?.format('MM/DD')} - {dateRange[1]?.format('MM/DD')}
							</span>
						)}
						{statusFilter !== FilterStatus.ALL && (
							<span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
								Status: {statusFilter}
							</span>
						)}
						{durationFilter !== FilterStatus.ALL && (
							<span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
								Duration: {durationFilter}
							</span>
						)}
						<button
							onClick={clearAllFilters}
							className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-200 rounded hover:bg-red-50"
						>
							Clear All
						</button>
					</div>
				)}

				{isInitialLoading && (
					<div className="mt-2">
						<div className="h-0.5 bg-blue-200 rounded-full overflow-hidden">
							<div className="h-full bg-blue-500 rounded-full animate-pulse" />
						</div>
					</div>
				)}
			</div>

			<div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
				{activeTab !== 'Intake Forms' && (
					<div
						className={`
				w-full lg:w-80 bg-white border-r border-gray-200 flex flex-col h-full
				${showMobileDetails ? 'hidden lg:block' : 'block'}
			`}
					>
						<div className="flex-1 overflow-y-auto h-[calc(100vh-140px)] lg:h-auto">
							{isLoadingCallLogs ? (
								<div className="flex items-center justify-center h-40">
									<div className="text-center">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2" />
										<p className="text-sm text-gray-500">Loading...</p>
									</div>
								</div>
							) : (
								<LogsList
									logs={filteredLogs}
									selectedLog={selectedLog}
									onSelectLog={handleLogSelect}
									onDeleteLog={handleDeleteLog}
								/>
							)}
							{hasNextPage && (activeTab === 'Calls' || activeTab === 'All Inbox') && (
								<div className="border-t border-gray-200">
									<button
										onClick={() => fetchNextPage()}
										disabled={isFetchingNextPage || !canLoadMore}
										className="w-full p-4 text-sm font-medium text-blue-600 hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-400"
									>
										{isFetchingNextPage
											? 'Loading...'
											: !canLoadMore
												? isLogsUnlimited
													? 'Load More'
													: `Call Logs Limit Reached (${totalCallCount}/${logsLimit})`
												: 'Load More'}
									</button>
									{!canLoadMore && !isLogsUnlimited && (
										<div className="px-4 py-2 text-xs text-gray-500 bg-gray-50">
											<div className="flex flex-col gap-2">
												<p>
													{isBasicPlan
														? "You've reached your call logs limit. Upgrade your plan to load more call logs."
														: 'You have reached your call logs limit. Please contact sales to get custom offers.'}
												</p>
												<button
													onClick={() =>
														handleActionButtonClick(isBasicPlan ? 'upgrade' : 'contact')
													}
													className="w-full px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded border border-blue-600 hover:border-blue-700 transition-colors"
												>
													{isBasicPlan ? 'Upgrade Plan' : 'Contact Sales'}
												</button>
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				)}

				<div
					className={`
			flex-1 bg-white h-full
			${!showMobileDetails ? 'hidden lg:block' : 'block'}
		`}
				>
					{activeTab === 'Intake Forms' && !showingFormResponse ? (
						<IntakeFormsPanel formGroups={formGroups} onFormSelect={handleFormSelect} />
					) : activeTab === 'Intake Forms' && showingFormResponse ? (
						<div className="h-full bg-white flex flex-col">
							<div className="px-4 sm:px-6 py-4 border-b border-gray-200">
								<button
									onClick={handleBackToForms}
									className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
								>
									← Back to Forms
								</button>
							</div>
							<div className="flex-1 overflow-y-auto">
								{selectedLog?.formGroup && (
									<div className="p-4 sm:p-6">
										<IntakeFormsDisplay
											formGroup={selectedLog.formGroup}
											agentId={selectedAgentId}
										/>
									</div>
								)}
							</div>
						</div>
					) : isLoadingCallLogs ? (
						<div className="h-full bg-white flex items-center justify-center">
							<div className="text-center">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
								<p className="text-gray-500">Loading...</p>
							</div>
						</div>
					) : (
						<div className="h-full">
							<div className="lg:hidden px-4 py-2 border-b border-gray-200">
								<button
									onClick={handleBackToList}
									className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2"
								>
									← Back to List
								</button>
							</div>
							<LogDetails log={selectedLog} debrand={debrand} isNoviUser={isNoviUser} />
						</div>
					)}
				</div>
			</div>

			<Modal
				title={
					<div className="flex items-center gap-2">
						<ExclamationCircleOutlined className="text-red-500" />
						<span>Confirm Delete</span>
					</div>
				}
				open={confirmDelete}
				onOk={() => handleConfirmDelete(selectedAgentId as string)}
				onCancel={handleCancelDelete}
				okText="Delete"
				cancelText="Cancel"
				okButtonProps={{
					danger: true,
					loading: isDeletingLog,
				}}
				cancelButtonProps={{
					disabled: isDeletingLog,
				}}
				closable={!isDeletingLog}
				maskClosable={!isDeletingLog}
			>
				<div className="py-4">
					<p className="text-gray-700 mb-2">
						Are you sure you want to delete this{' '}
						<span className="font-semibold text-gray-900">{logToDelete?.type}</span> log?
					</p>
					<p className="text-sm text-gray-500">This action cannot be undone.</p>
				</div>
			</Modal>
		</div>
	);
};

export default Inbox;
